import { useMutation, useQuery } from '@apollo/client';
import { DataTable } from '@components/DataTable';
import { useContainerHeight } from '@components/DataTable/hooks';
import { DataTableColumn, SelectionModel } from '@components/DataTable/types';
import ConfirmationDialog from '@components/Dialog/Confirm';
import { FilterSelectMenu } from '@components/Filter';
import { SearchBarWithNumber } from '@components/SearchbarWithNumber';
import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import { usePageSize } from '@hooks/usePageSize';
import { DELETE_UPERCUT, GET_UPERCUTS } from '@gql/upercut';
import { GET_UPERCUT_ROLES } from '@gql/upercut-permissions';
import {
    Avatar,
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    IconButton,
    MenuItem,
    Stack,
    Tooltip,
    Typography
} from '@mui/material';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { UpercutItem } from './types';

interface RoleOption {
    _id: string;
    name: string;
}

const UpercutManagement = () => {
    const navigate = useNavigate();
    const [page, setPage] = useState(0);
    const { pageSize: rowsPerPage, storePageSize } = usePageSize({ storageKey: 'upercuts-page-size' });
    const [selectionModel, setSelectionModel] = useState<SelectionModel>({
        selectedRows: [],
        isAllSelected: false,
        isSomeSelected: false,
    });
    const [openModal, setOpenModal] = useState({
        open: false,
        title: '',
        description: '',
        action: '',
        cancelTitle: '',
        confirmTitle: '',
    });
    const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
    const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
    const [selectedRoleFilter, setSelectedRoleFilter] = useState<string[]>([]);

    const { containerRef, containerHeight } = useContainerHeight();
    const { showSnackbar } = useSnackbar();

    const { data, loading, refetch } = useQuery(GET_UPERCUTS, {
        notifyOnNetworkStatusChange: true,
        variables: {
            offset: page * rowsPerPage,
            limit: rowsPerPage,
            filter: selectedRoleFilter.length > 0 ? { roleIds: selectedRoleFilter } : null,
        },
    });

    const [deleteUpercut] = useMutation(DELETE_UPERCUT);

    const { data: rolesData } = useQuery(GET_UPERCUT_ROLES, {
        fetchPolicy: 'cache-and-network',
    });
    const roles: RoleOption[] = rolesData?.upercutRoles ?? [];

    const upercuts: UpercutItem[] = useMemo(() => {
        return data?.getUpercuts?.upercuts ?? [];
    }, [data]);

    const total = data?.getUpercuts?.total ?? 0;

    useEffect(() => {
        setSelectionModel({ selectedRows: [], isAllSelected: false, isSomeSelected: false });
    }, [page, rowsPerPage]);

    const handleRoleFilterChange = (roleId: string) => {
        setSelectedRoleFilter((prev) => {
            if (prev.includes(roleId)) {
                return prev.filter((id) => id !== roleId);
            } else {
                return [...prev, roleId];
            }
        });
        setPage(0);
    };

    const requestDelete = useCallback((id: string) => {
        setPendingDeleteId(id);
        const upercut = upercuts.find(u => u._id === id);
        setOpenModal({
            open: true,
            title: 'Supprimer un utilisateur',
            description: `Êtes-vous sûr de vouloir supprimer l'utilisateur ${upercut?.firstName} ${upercut?.lastName} ?`,
            action: 'delete',
            cancelTitle: 'Annuler',
            confirmTitle: 'Supprimer',
        });
    }, [upercuts]);

    const handleModalAction = async () => {
        if (openModal.action !== 'delete' || !pendingDeleteId) {
            return;
        }
        setIsLoadingSubmit(true);
        try {
            await deleteUpercut({
                variables: {
                    id: pendingDeleteId,
                },
            });
            showSnackbar('Utilisateur supprimé avec succès.', 'success');
            await refetch();
        } catch (err: any) {
            showSnackbar(err?.message || "Échec de la suppression de l'utilisateur.", 'error');
        } finally {
            setIsLoadingSubmit(false);
            setOpenModal({
                open: false,
                title: '',
                description: '',
                action: '',
                cancelTitle: '',
                confirmTitle: '',
            });
            setPendingDeleteId(null);
        }
    };

    const columns = useMemo<DataTableColumn<UpercutItem>[]>(() => [
        {
            id: 'user',
            label: 'Utilisateur',
            wrap: true,
            renderCell: ({ row }) => (
                <Stack flexDirection={'row'} alignItems="center" gap={2} sx={{ minWidth: 0, width: '100%' }}>
                    <Avatar
                        sx={{ width: 36, height: 36, bgcolor: 'primary.main', mt: 0.25 }}
                    >
                        {row.firstName?.[0]?.toUpperCase()}{row.lastName?.[0]?.toUpperCase()}
                    </Avatar>
                    <Stack>
                        <Typography
                            variant="body2"
                            sx={{ fontSize: 14, fontWeight: 500 }}
                        >
                            {row.firstName} {row.lastName}
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontSize: 12 }}
                        >
                            {row.email}
                        </Typography>
                    </Stack>
                </Stack>
            ),
        },
        {
            id: 'role',
            label: 'Rôle',
            align: 'center',
            renderCell: ({ row }) => (
                <Typography variant="body2" sx={{ fontSize: 14 }}>
                    {row.assignedRole?.role?.name || '-'}
                </Typography>
            ),
        },
        {
            id: 'createdDate',
            label: 'Date de création',
            align: 'center',
            renderCell: ({ row }) => (
                <Typography variant="body2" sx={{ fontSize: 14 }}>
                    {moment(row.createdDate).format('DD/MM/YYYY')}
                </Typography>
            ),
        },
        {
            id: 'updatedDate',
            label: 'Dernière modification',
            align: 'center',
            renderCell: ({ row }) => (
                <Typography variant="body2" sx={{ fontSize: 14 }}>
                    {row.updatedDate ? moment(row.updatedDate).format('DD/MM/YYYY') : '-'}
                </Typography>
            ),
        },
        {
            id: 'actions',
            label: 'Actions',
            align: 'center',
            renderCell: ({ row }) => (
                <Box display="flex" justifyContent="center" gap={1}>
                    <Tooltip title="Modifier">
                        <IconButton
                            sx={{ width: 32, height: 32 }}
                            size="small"
                            variant="contained"
                            color="primary"
                            onClick={() => navigate(`/upercuts/${row._id}/edit`)}
                        >
                            <i className="fi fi-br-pencil" style={{ fontSize: 14 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Supprimer">
                        <IconButton
                            sx={{ width: 32, height: 32 }}
                            size="small"
                            variant="contained"
                            color="error"
                            onClick={() => requestDelete(row._id)}
                        >
                            <i className="fi fi-br-trash" style={{ fontSize: 14 }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            ),
        },
    ], [navigate, requestDelete]);

    return (
        <Box
            p={3}
            minHeight="calc(100vh - 118px)"
            sx={{
                display: 'flex',
                flexDirection: 'column',
            }}>
            <ConfirmationDialog
                details={openModal}
                setOpen={setOpenModal}
                handleModalAction={handleModalAction}
                isLoadingSubmit={isLoadingSubmit}
            />

            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
                <Stack direction="row" alignItems="center" gap={2}>
                    <SearchBarWithNumber totalCount={total} onChange={() => { }} />
                    <FilterSelectMenu
                        value={selectedRoleFilter}
                        onChange={() => { }}
                        displayEmpty
                        renderValue={(selected) => {
                            if (!Array.isArray(selected) || selected.length === 0) {
                                return 'Filtrer par rôle';
                            }
                            return `${selected.length} rôle(s) sélectionné(s)`;
                        }}
                    >
                        {roles.map((role) => (
                            <MenuItem key={role._id} value={role._id}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={selectedRoleFilter.includes(role._id)}
                                        />
                                    }
                                    label={role.name}
                                    sx={{ mr: 1, ml: 0 }}
                                    onClick={(e) => {e.stopPropagation(); handleRoleFilterChange(role._id);}}
                                />
                            </MenuItem>
                        ))}
                    </FilterSelectMenu>
                </Stack>
                <Stack direction="row" alignItems="center" gap={2}>
                    <Button size="large" variant="outlined" color="secondary"
                        component={Link} to="/upercuts/roles"
                    >
                        Gestion des rôles
                    </Button>
                    <Button
                        startIcon={<i className="fi fi-br-plus" style={{ fontSize: 12 }}></i>}
                        size="large"
                        variant="contained"
                        onClick={() => navigate('/upercuts/create')}
                    >
                        Ajouter
                    </Button>
                </Stack>
            </Stack>

            <Box
                ref={containerRef}
                sx={{
                    flex: 1,
                    minHeight: 0,
                }}>
                <DataTable
                    stickyHeader
                    rows={upercuts}
                    columns={columns}
                    loading={loading}
                    checkboxSelection
                    selectionModel={selectionModel}
                    onSelectionModelChange={setSelectionModel}
                    height={containerHeight}
                    pagination={{
                        currentPage: page + 1,
                        pageSize: rowsPerPage,
                        totalRows: total,
                        onPageChange: (newPage) => setPage(newPage - 1),
                        onPageSizeChange: (newSize) => {
                            storePageSize(newSize);
                            setPage(0);
                        },
                    }}
                />
            </Box>
        </Box>
    );
};

export default UpercutManagement;
